// Copyright (c) Ping. All rights reserved.
//
// Device-contacts provider (browser). Feeds the "YOUR CONTACTS" section in
// New chat / New group and the `/contacts` screen. Browsers cannot read the
// OS address book directly (no API), so we source contacts two ways and
// persist them per-device in localStorage:
//   1. Contact Picker API (`navigator.contacts.select`) — Chromium mobile
//      only, user-gesture gated.
//   2. vCard / .vcf import — universal (Google/Apple/Outlook all export it).
// The NATIVE builds (apps/desktop, iOS/Android) read the address book via
// `react-native-contacts` in their own provider; this shared shape
// (`DeviceContact`) is what both feed into the modals' `source: 'contact'`
// rows.

const STORE_KEY = 'ping.deviceContacts.v1';

export interface DeviceContact {
  /** Stable per-device id (the identifier, or a synthesised key). */
  readonly id: string;
  readonly displayName: string;
  /** Email or E.164 phone used to resolve the contact to a Ping account. */
  readonly identifier: string;
  readonly avatarUri?: string | undefined;
}

function readStore(): DeviceContact[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(STORE_KEY);
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as DeviceContact[]) : [];
  } catch {
    return [];
  }
}

function writeStore(list: readonly DeviceContact[]): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORE_KEY, JSON.stringify(list));
    }
  } catch {
    /* quota / unavailable — contacts just don't persist this session */
  }
}

/** All stored device contacts, sorted by display name. */
export function loadDeviceContacts(): DeviceContact[] {
  return [...readStore()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/** Merge new contacts into the store (dedup by identifier), return the union. */
export function mergeDeviceContacts(incoming: readonly DeviceContact[]): DeviceContact[] {
  const byId = new Map<string, DeviceContact>();
  for (const c of readStore()) byId.set(c.identifier.toLowerCase(), c);
  for (const c of incoming) {
    if (c.identifier.length === 0) continue;
    byId.set(c.identifier.toLowerCase(), c);
  }
  const merged = Array.from(byId.values());
  writeStore(merged);
  return merged.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/** Remove one contact by id. */
export function removeDeviceContact(id: string): DeviceContact[] {
  const next = readStore().filter((c) => c.id !== id);
  writeStore(next);
  return next;
}

/** Case-insensitive name/identifier filter. */
export function filterDeviceContacts(
  contacts: readonly DeviceContact[],
  query: string,
): DeviceContact[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [...contacts];
  return contacts.filter(
    (c) =>
      c.displayName.toLowerCase().includes(q) || c.identifier.toLowerCase().includes(q),
  );
}

/** True when the browser exposes the Contact Picker API (Chromium mobile). */
export function isContactPickerSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'contacts' in navigator &&
    typeof (navigator as { contacts?: { select?: unknown } }).contacts?.select === 'function'
  );
}

interface PickedContact {
  name?: string[];
  email?: string[];
  tel?: string[];
}

/** Open the OS Contact Picker (where supported) and merge the result. */
export async function pickDeviceContacts(): Promise<DeviceContact[]> {
  if (!isContactPickerSupported()) return loadDeviceContacts();
  try {
    const api = (navigator as unknown as {
      contacts: { select: (props: string[], opts: { multiple: boolean }) => Promise<PickedContact[]> };
    }).contacts;
    const picked = await api.select(['name', 'email', 'tel'], { multiple: true });
    const mapped: DeviceContact[] = [];
    for (const p of picked) {
      const identifier = p.email?.[0] ?? p.tel?.[0] ?? '';
      if (identifier.length === 0) continue;
      const displayName = p.name?.[0] ?? identifier;
      mapped.push({ id: identifier, displayName, identifier });
    }
    return mergeDeviceContacts(mapped);
  } catch {
    return loadDeviceContacts();
  }
}

/** Parse a vCard (.vcf) text blob into device contacts and merge them. */
export function importVCard(text: string): DeviceContact[] {
  const cards: DeviceContact[] = [];
  // Split on END:VCARD; each card is line-based key[;params]:value.
  const blocks = text.split(/END:VCARD/i);
  for (const block of blocks) {
    if (!/BEGIN:VCARD/i.test(block)) continue;
    let name = '';
    let email = '';
    let tel = '';
    for (const rawLine of block.split(/\r?\n/)) {
      const line = rawLine.trim();
      const colon = line.indexOf(':');
      if (colon < 0) continue;
      const key = line.slice(0, colon).toUpperCase();
      const value = line.slice(colon + 1).trim();
      if (key === 'FN' && name.length === 0) name = value;
      else if (key.startsWith('EMAIL') && email.length === 0) email = value;
      else if (key.startsWith('TEL') && tel.length === 0) tel = value.replace(/[^\d+]/g, '');
    }
    const identifier = email || tel;
    if (identifier.length === 0) continue;
    cards.push({ id: identifier, displayName: name || identifier, identifier });
  }
  return mergeDeviceContacts(cards);
}

/** Prompt for a .vcf file and import it (browser file input). Resolves to
 *  the merged contact list (unchanged if the user cancels). */
export function importVCardViaFilePicker(): Promise<DeviceContact[]> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(loadDeviceContacts());
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.vcf,text/vcard,text/x-vcard';
    input.multiple = true;
    input.onchange = () => {
      const files = Array.from(input.files ?? []);
      if (files.length === 0) {
        resolve(loadDeviceContacts());
        return;
      }
      void (async () => {
        let merged = loadDeviceContacts();
        for (const f of files) {
          try {
            merged = importVCard(await f.text());
          } catch {
            /* skip bad file */
          }
        }
        resolve(merged);
      })();
    };
    input.click();
  });
}
